import React, {useState, useRef, useEffect} from "react";
import {
  Typography,
  ListItemText,
  IconButton,
  TextField,
  Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CloseIcon from "@material-ui/icons/Close";

interface EditableHeadingProps {
  value: string;
  onChange: (newValue: string) => void;
  hint?: string;
}

const styles = {
  fileName: {
    color: "#424242",
    fontSize: "1.125rem",
    fontWeight: 500,
  },
};

const EditableHeading: React.FC<EditableHeadingProps> = ({
  value,
  onChange,
  hint,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSave = () => {
    if ((draft ?? "").trim()) {
      onChange(draft.trim());
    }
    setIsEditing(false);
  };

  useEffect(() => {
    if (draft !== value) {
      setDraft(value);
    }
  }, [value]);

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleSave();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, draft]);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      {isEditing ? (
        <TextField
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              handleSave();
            }
            if (e.key === "Escape") {
              setIsEditing(false);
            }
          }}
          size="small"
          autoFocus
        />
      ) : (
        <Typography variant="body1" style={{flex: 1, minWidth: 0}}>
          <ListItemText
            primary={<span style={styles.fileName}>{value}</span>}
            style={{marginRight: 0}}
          />
        </Typography>
      )}
      {hint && (
        <Tooltip title={hint}>
          <HelpOutlineIcon style={{color: "rgba(0,0,0,0.54)"}} />
        </Tooltip>
      )}
      <IconButton
        size="small"
        onClick={() => (isEditing ? setIsEditing(false) : setIsEditing(true))}
      >
        {isEditing ? <CloseIcon /> : <EditIcon />}
      </IconButton>
    </div>
  );
};

export default EditableHeading;
